import {
  DisputeOutcome,
  type OrderDto,
  type OrderPartyDto,
  TEXT_LIMITS,
  values,
} from '@posilka/shared';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@posilka/client/api/client';
import { orderKeys, resolveDispute, useOrderMutation } from '@posilka/client/api/orders';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { Section } from '@/components/Section';
import { haptic } from '@/lib/telegram';
import { CharHint } from '@/components/Field';

function BlockButton({ party, orderId }: { party: OrderPartyDto; orderId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () =>
      api(`/admin/users/${party.id}/${party.isBlocked ? 'unblock' : 'block'}`, { method: 'POST' }),
    onSuccess: () => {
      haptic.success();
      void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
    },
    onError: () => haptic.error(),
  });
  return (
    <div className="flex items-center justify-between border-b border-line px-5 py-2.5 last:border-b-0">
      <span>
        {party.firstName}
        {party.isBlocked && (
          <span className="ml-2 text-xs text-destructive">{t('admin.blocked')}</span>
        )}
      </span>
      <button
        type="button"
        className={`text-sm ${party.isBlocked ? 'text-link' : 'text-destructive'}`}
        disabled={toggle.isPending}
        onClick={() => toggle.mutate()}
      >
        {party.isBlocked ? t('admin.unblock') : t('admin.block')}
      </button>
    </div>
  );
}

/** Nizoli buyurtma: natija + izoh bilan yopish (tomonlarga bot orqali boradi). */
function ResolveDispute({ order }: { order: OrderDto }) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const [outcome, setOutcome] = useState<DisputeOutcome>(DisputeOutcome.LOST_NO_FAULT);
  const [note, setNote] = useState('');
  const resolve = useOrderMutation(() => resolveDispute(order.id, outcome, note.trim()));
  return (
    <Section title={`⚖️ ${t('dispute.resolve')}`}>
      <div className="space-y-2 p-3">
        <select
          className="input"
          aria-label={t('dispute.outcome')}
          value={outcome}
          onChange={(e) => setOutcome(e.target.value as DisputeOutcome)}
        >
          {values(DisputeOutcome).map((o) => (
            <option key={o} value={o}>
              {t(`dispute.outcomes.${o}`)}
            </option>
          ))}
        </select>
        {outcome === DisputeOutcome.LOST_DRIVER_FAULT && (
          <p className="px-1 text-sm text-destructive">{t('dispute.driverFaultHint')}</p>
        )}
        <textarea
          className="input min-h-20 resize-none"
          placeholder={t('dispute.note')}
          maxLength={TEXT_LIMITS.disputeNote.max}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <CharHint count={{ value: note, ...TEXT_LIMITS.disputeNote }} />
        <button
          type="button"
          className="btn-secondary w-full text-link"
          disabled={note.trim().length < TEXT_LIMITS.disputeNote.min || resolve.isPending}
          onClick={() =>
            resolve.mutate(undefined, {
              onSuccess: () => haptic.success(),
              onError: () => haptic.error(),
            })
          }
        >
          {t('dispute.submit')}
        </button>
        {resolve.isError && (
          <p className="text-sm text-destructive">{errorMessage(resolve.error)}</p>
        )}
      </div>
    </Section>
  );
}

/** Admin ko'rinishida: nizoni hal qilish va tomonlarni bloklash (SPEC §11, §19). */
export function AdminOrderTools({ order }: { order: OrderDto }) {
  const { t } = useTranslation();
  if (order.viewer.role !== 'admin') return null;
  return (
    <>
      {order.status === 'DISPUTED' && <ResolveDispute order={order} />}
      <Section title={t('admin.title')}>
        {order.sender && <BlockButton party={order.sender} orderId={order.id} />}
        {order.driver && <BlockButton party={order.driver} orderId={order.id} />}
      </Section>
    </>
  );
}
